import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { fetchReceipts } from '../../../redux/Thunk/Receipt/ReceiptThunks';
import { fetchDoctorQuickOrders } from '../../../redux/Thunk/QuickOrder/QuickOrderThunks';
import SidebarToggle from '../SidebarToggle';
import CheckedBg from "../../common/icons/CheckedBg";
import Close from "../../common/icons/Close";
import Timing from "../../common/icons/Timing";
import Verifed from "../../common/icons/Verifed";
import CloseBg from "../../common/icons/CloseBg";
import { fetchRecentPrescriptions } from '../../../redux/Thunk/Prescription/PrescriptionThunks';
import { fetchOrdersOfDoctor } from '../../../redux/Thunk/Order/OrderThunks';

const QuickTrackingWidget = ({ rapidScanSlot, isAIGenerated }) => {
  const dispatch = useDispatch();

  // Last Order Logic
  const lastOrder = useSelector((state) => state.doctor?.doctorDetails?.lastOrder);
  const storeDetail = useSelector((state) => state.store?.storeDetail);
  const storeOrder = storeDetail?.lastOrder;
  const LastOrderId = useSelector((state) => state.order?.lastOrderId);
  const orderById = useSelector((state) => state.order?.orderById);
  const ordersList = useSelector((state) => state.order?.orders?.orders);

  const actualLastOrderId = orderById?.OID || LastOrderId || ordersList?.[0]?.OID || lastOrder?.OID || storeOrder?.OID || null;
  const actualLastOrderDate = orderById?.createdAt ? moment(orderById.createdAt).format("DD/MM/YYYY") : (ordersList?.[0]?.createdAt ? moment(ordersList[0].createdAt).format("DD/MM/YYYY") : (lastOrder?.createdAt ? moment(lastOrder.createdAt).format("DD/MM/YYYY") : "-"));

  // Last Receipt Logic
  const lastReceipt = useSelector((state) => state.receipt?.lastCreatedReceipt);
  const receiptsList = useSelector((state) => state.receipt?.receipts);
  const doctor = useSelector((state) => state.doctor?.doctorDetails?.doctor);
  const actualLastReceiptId = lastReceipt?.RID || receiptsList?.[0]?.RID || doctor?.lastReceipt?.RID || null;
  const actualLastReceiptDate = lastReceipt?.createdAt ? moment(lastReceipt.createdAt).format("DD/MM/YYYY") : (receiptsList?.[0]?.createdAt ? moment(receiptsList[0].createdAt).format("DD/MM/YYYY") : doctor?.lastReceipt?.createdAt ? moment(doctor.lastReceipt.createdAt).format("DD/MM/YYYY") : "-");

  // Last Prescription Logic
  const lastCreatedPrescription = useSelector((state) => state.prescription?.lastCreatedPrescription);
  const recentPrescriptions = useSelector((state) => state.prescription?.recentPrescriptions);

  // Use lastCreated as priority, then fallback to recent list
  const actualLastPrescriptionId = lastCreatedPrescription?.XID || recentPrescriptions?.[0]?.XID || doctor?.lastPrescription?.XID || null;
  const actualLastPrescriptionDate = lastCreatedPrescription?.createdAt ? moment(lastCreatedPrescription.createdAt).format("DD/MM/YYYY") : (recentPrescriptions?.[0]?.createdAt ? moment(recentPrescriptions[0].createdAt).format("DD/MM/YYYY") : (doctor?.lastPrescription?.createdAt ? moment(doctor.lastPrescription.createdAt).format("DD/MM/YYYY") : "-"));

  // Last Quick Order Logic
  const quickOrdersList = useSelector((state) => state.quickOrder?.quickOrders);
  const actualLastQuickOrderId = quickOrdersList?.[0]?.QID || null;
  const actualLastQuickOrderDate = quickOrdersList?.[0]?.createdAt ? moment(quickOrdersList[0].createdAt).format("DD/MM/YYYY") : "-";

  useEffect(() => {
    if (doctor?.DID) {
      // Always fetch latest records on mount to ensure robustness
      dispatch(fetchReceipts({ DID: doctor.DID, page: 1, limit: 1 }));
      dispatch(fetchRecentPrescriptions({ DID: doctor.DID }));
      dispatch(fetchOrdersOfDoctor({ page: 1, limit: 1, filterType: 'all' }));
      dispatch(fetchDoctorQuickOrders());
    }
  }, [dispatch, doctor?.DID]);

  const is_pin_b = doctor?.is_pin_b;

  const formatText = (text = "") =>
    text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

  return (
    <div className="w-full bg-white border border-gray-200 rounded-[12px] p-2.5 sm:p-3.5 shadow-xs mb-3 flex flex-col gap-2.5 font-sans">
      {/* Top Header Block: Profile, Statuses, Rapid Scan */}
      <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-2.5 pb-2.5 border-b border-gray-100">
        {/* Left Section: Menu button, Logo-brandname & Page Name, Divider, Rapid-Smart Status Pill */}
        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 min-w-0">
          <SidebarToggle className="!p-0 !min-w-0 mr-0.5" />
          
          {/* Brand Logo & Wordmark + Page Name */}
          <div className="flex flex-col gap-1 shrink-0 justify-center">
            <div className="flex items-center gap-1.5 shrink-0">
              <img src="/logo.png" alt="logo" className="h-[18px] sm:h-[22px] w-auto object-contain shrink-0" />
              <img src="/logo.svg" alt="Hyperapt" className="h-[15px] sm:h-[18px] w-auto max-w-[90px] sm:max-w-[105px] object-contain shrink-0" />
            </div>
            <span className="text-[17px] sm:text-[21px] font-black text-gray-900 tracking-tight leading-none">
              Studio
            </span>
          </div>

          {/* Vertical Partition */}
          <div className="h-7 w-[1px] bg-gray-200 hidden sm:block"></div>

          {/* Single Consolidated Status Pill */}
          <div className="flex flex-col gap-1 bg-primary-50/80 border border-primary-100/80 px-3 py-1.5 rounded-[9px] text-[10px] sm:text-[11px] font-bold shrink-0">
            {/* Line 1: Rapid Fulfillment */}
            <div className="flex items-center justify-between gap-2.5">
              <span className="text-[8px] sm:text-[9px] text-gray-400 font-extrabold uppercase tracking-wider">Rapid Fulfillment:</span>
              <div className="flex items-center gap-1.5">
                <div className="flex-shrink-0 flex items-center justify-center">
                  {doctor?.store?.currentStoreStatus === "AWAY" ? (
                    <Timing fill="#cba923" height={14} width={14} />
                  ) : doctor?.store?.currentStoreStatus === "ACTIVE" ? (
                    <CheckedBg fill="#21b446" height={14} width={14} />
                  ) : doctor?.store?.currentStoreStatus === "CLOSED" ? (
                    <Close fill="#c42727" height={14} width={14} />
                  ) : null}
                </div>
                <span className="leading-none uppercase text-primary-800 text-[10px] sm:text-[11px]">{formatText(doctor?.store?.currentStoreStatus)}</span>
              </div>
            </div>

            {/* Line 2: Smart Order */}
            <div className="flex items-center justify-between gap-2.5 border-t border-primary-100/70 pt-1">
              <span className="text-[8px] sm:text-[9px] text-gray-400 font-extrabold uppercase tracking-wider">Smart Order:</span>
              <div className="flex items-center gap-1.5">
                {doctor?.autoSignature ? (
                  <Verifed height={14} width={14} fill="#00C732" />
                ) : (
                  <CloseBg height={14} width={14} fill="#d16c6c" />
                )}
                <span className={`leading-none text-[10px] sm:text-[11px] ${doctor?.autoSignature ? 'text-green-600' : 'text-red-500'}`}>
                  {doctor?.autoSignature ? "ON" : "OFF"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section: Rapid Scan Action Button */}
        {rapidScanSlot && (
          <div className="shrink-0 max-sm:w-full">
            {rapidScanSlot}
          </div>
        )}
      </div>

      {/* Activity Stats Summary Bar ("Last IDs block") */}
      <div className="bg-success-50/70 border border-success-200/60 rounded-[10px] py-2 px-1 sm:px-3 flex items-center justify-between shadow-2xs backdrop-blur-sm overflow-x-auto scrollbar-hide">
        <div className={`flex-1 flex flex-col items-center border-success-200/50 px-2 min-w-[85px] sm:min-w-0 ${!is_pin_b ? 'border-r' : ''}`}>
          <span className="text-[8px] sm:text-[9px] uppercase font-extrabold text-purple-600 mb-0.5 tracking-widest whitespace-nowrap text-center">Last Rx</span>
          <h2 className="text-[14px] sm:text-[16px] font-bold text-secondary-700 leading-tight">{actualLastPrescriptionId ? `${actualLastPrescriptionId}` : '-'}</h2>
          <span className="text-[9px] sm:text-[10px] text-gray-400 font-medium whitespace-nowrap">{actualLastPrescriptionId ? actualLastPrescriptionDate : 'No Record'}</span>
        </div>
        <div className={`flex-1 flex flex-col items-center px-2 min-w-[85px] sm:min-w-0 ${!is_pin_b ? 'border-r border-success-200/50' : ''}`}>
          <span className="text-[8px] sm:text-[9px] uppercase font-extrabold text-blue-600 mb-0.5 tracking-widest whitespace-nowrap text-center">Last Receipt</span>
          <h2 className="text-[14px] sm:text-[16px] font-bold text-secondary-700 leading-tight">{actualLastReceiptId ? `${actualLastReceiptId}` : '-'}</h2>
          <span className="text-[9px] sm:text-[10px] text-gray-400 font-medium whitespace-nowrap">{actualLastReceiptId ? actualLastReceiptDate : 'No Record'}</span>
        </div>
        {!is_pin_b && (
          <div className="flex-1 flex flex-col items-center border-r border-success-200/50 px-2 min-w-[85px] sm:min-w-0">
            <span className="text-[8px] sm:text-[9px] uppercase font-extrabold text-green-600 mb-0.5 tracking-widest whitespace-nowrap text-center">Last Order</span>
            <h2 className="text-[14px] sm:text-[16px] font-bold text-secondary-700 leading-tight">{actualLastOrderId ? `${actualLastOrderId}` : '-'}</h2>
            <span className="text-[9px] sm:text-[10px] text-gray-400 font-medium whitespace-nowrap">{actualLastOrderId ? actualLastOrderDate : 'No Record'}</span>
          </div>
        )}
        {!is_pin_b && (
          <div className="flex-1 flex flex-col items-center px-2 min-w-[85px] sm:min-w-0">
            <span className="text-[8px] sm:text-[9px] uppercase font-extrabold text-orange-500 mb-0.5 tracking-widest whitespace-nowrap text-center">Last Quick Order</span>
            <h2 className="text-[14px] sm:text-[16px] font-bold text-secondary-700 leading-tight">{actualLastQuickOrderId ? `${actualLastQuickOrderId}` : '-'}</h2>
            <span className="text-[9px] sm:text-[10px] text-gray-400 font-medium whitespace-nowrap">{actualLastQuickOrderId ? actualLastQuickOrderDate : 'No Record'}</span>
          </div>
        )}
      </div>

      {/* Doctor Name Card Row (After Last IDs Block) */}
      <div className="bg-gradient-to-r from-primary-50/90 via-primary-50/50 to-white border border-primary-100/90 px-3 py-1.5 rounded-[9px] flex items-center justify-between">
        <h3 className="text-[13px] sm:text-[14px] font-bold text-secondary-800 tracking-tight whitespace-nowrap">
          Dr. {doctor?.personalInfo?.name} {doctor?.personalInfo?.surname}
        </h3>
      </div>

      {/* Embedded AI Warning Banner */}
      {isAIGenerated && (
        <div className="w-full bg-red-50 border border-red-200 rounded-[10px] p-3 flex items-start gap-2.5 mt-0.5">
          <div className="mt-0.5 text-red-600 shrink-0">
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          </div>
          <p className="text-[12px] sm:text-[13px] text-red-800 leading-snug">
            <span className="font-bold">Intelligently Generated Order:</span> This order was generated by our intelligence engine. The engine can make mistakes, so it is <strong>mandatory</strong> that you thoroughly check and verify all generated patient and medicine details before placing the order.
          </p>
        </div>
      )}
    </div>
  );
};
export default QuickTrackingWidget;
